import { useMemo, useState } from "react";
import districtMap from "@/components/maharashtraDistrictPaths.json";

const VIEWBOX = districtMap?.viewBox || [0, 0, 900, 702];
const [VB_X, VB_Y, VB_W, VB_H] = VIEWBOX;
const DISTRICTS = districtMap?.districts || {};

const DistrictLabel = ({ region, isActive, isHovered }) => {
  const [lx, ly] = region.labelPoint || [];
  if (typeof lx !== "number" || typeof ly !== "number") return null;

  const label = region.label || region.key;
  const compact = ["Mumbai", "Mumbai Suburban", "Thane", "Palghar"].includes(region.key);
  const fontSize = compact ? 7.5 : 8.5;
  const show = isHovered || isActive;

  if (!show) return null;

  const padX = 5;
  const padY = 3;
  const textWidth = label.length * (fontSize * 0.52);
  const w = textWidth + padX * 2;
  const h = fontSize + padY * 2;

  return (
    <g pointerEvents="none" opacity={isHovered ? 1 : 0.92}>
      <polygon
        points={`${lx - 4},${ly - h / 2 - 1} ${lx - 1},${ly - h / 2 - 4} ${lx + 2},${ly - h / 2 - 1}`}
        fill="#16a34a"
      />
      <rect
        x={lx - w / 2}
        y={ly - h / 2}
        width={w}
        height={h}
        rx={h / 2}
        fill="rgba(255,255,255,0.96)"
        stroke="#16a34a"
        strokeWidth={1.2}
      />
      <text
        x={lx}
        y={ly}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize}
        fontWeight={700}
        fill="#14532d"
        style={{ userSelect: "none" }}
      >
        {label}
      </text>
    </g>
  );
};

const MaharashtraDistrictMapSvg = ({
  districtDataMap = {},
  selectedDistrict,
  onSelectDistrict,
  getDistrictFill,
}) => {
  const [hovered, setHovered] = useState(null);

  const regions = useMemo(
    () =>
      Object.entries(DISTRICTS)
        .map(([key, meta]) => ({ key, ...meta }))
        .sort((a, b) => a.key.localeCompare(b.key)),
    [],
  );

  const handleSelect = (districtName) => {
    onSelectDistrict?.(districtName);
  };

  return (
    <div className="ed-maha-map-wrap">
      <svg
        className="ed-maha-map-svg"
        viewBox={`${VB_X} ${VB_Y} ${VB_W} ${VB_H}`}
        role="img"
        aria-label="Maharashtra district map"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="ed-maha-shadow" x="-8%" y="-8%" width="116%" height="116%">
            <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#1f4a8b" floodOpacity="0.14" />
          </filter>
        </defs>

        <g filter="url(#ed-maha-shadow)">
          {regions.map((region) => (
            <path
              key={`outline-${region.key}`}
              d={region.path}
              fill="none"
              stroke="#dc2626"
              strokeWidth={3.2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          ))}

          {regions.map((region) => {
            const district = districtDataMap[region.key];
            const hasData = district?.has_data;
            const isSelected = region.key === selectedDistrict;
            const isHovered = region.key === hovered;
            const dimmed = hovered && !isHovered;
            const fill = getDistrictFill(region.key);

            return (
              <path
                key={region.key}
                d={region.path}
                fill={fill}
                fillOpacity={dimmed ? 0.42 : hasData ? 0.94 : 0.72}
                stroke={isSelected ? "#0f4bdc" : isHovered ? "#2563eb" : "#cbd5e1"}
                strokeWidth={isSelected ? 3 : isHovered ? 2.2 : 1.1}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                onMouseEnter={() => setHovered(region.key)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handleSelect(region.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(region.key);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`${region.key} district`}
                style={{
                  cursor: hasData ? "pointer" : "default",
                  transition: "fill-opacity 0.18s ease, stroke 0.18s ease, stroke-width 0.18s ease",
                }}
                data-testid={`district-${region.key.toLowerCase().replace(/\s+/g, "-")}`}
              />
            );
          })}
        </g>

        {regions.map((region) => (
          <DistrictLabel
            key={`label-${region.key}`}
            region={region}
            isHovered={hovered === region.key}
            isActive={selectedDistrict === region.key}
          />
        ))}
      </svg>
    </div>
  );
};

export default MaharashtraDistrictMapSvg;
